import { useEffect, useRef } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { isAxiosError } from 'axios';
import {
  cancelHygieneScan,
  fetchHygieneAIEstimate,
  fetchHygieneAssignees,
  fetchHygieneChecks,
  fetchHygieneFinding,
  fetchHygieneFindings,
  fetchHygieneScan,
  fetchHygieneSummary,
  postHygieneAction,
  startHygieneScan,
  type HygieneAction,
  type HygieneActionBody,
  type HygieneListParams,
  type HygieneScan,
} from '../../request/hygiene/hygieneRequests.ts';
import { getApiErrorMessage } from '../../request/getApiErrorMessage.ts';
import { isModuleAccessDeniedError } from '../../request/moduleAccessDenied.ts';
import { hygieneQueryKeys } from '../../queries/hygiene-check/keys.ts';

/** One toast per failure: the axios interceptor already toasts module-access denials. */
function toastError(error: unknown, fallback: string): void {
  if (!isModuleAccessDeniedError(error)) toast.error(getApiErrorMessage(error, fallback));
}

/**
 * React Query hooks for Hygiene Check. Reads never write on the server; the page asks for a rule check when the
 * summary says the year is stale. Hygiene endpoints answer errors as { error }, which the axios interceptor does
 * not toast, so these hooks show the one toast.
 */

const FINISHED = new Set(['completed', 'failed', 'cancelled']);

export function isScanActive(scan?: HygieneScan | null): boolean {
  return Boolean(scan && !FINISHED.has(scan.status));
}

export function useHygieneSummary(auditYearId: string | null) {
  return useQuery({
    queryKey: hygieneQueryKeys.summary(auditYearId ?? ''),
    queryFn: () => fetchHygieneSummary(auditYearId as string),
    enabled: Boolean(auditYearId),
    // Always refetch on open: the summary says whether the year is stale, which decides whether to re-check.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useHygieneFindings(params: HygieneListParams | null) {
  return useQuery({
    queryKey: params ? hygieneQueryKeys.findings(params) : ['hygiene-check', 'none'],
    queryFn: () => fetchHygieneFindings(params as HygieneListParams),
    enabled: Boolean(params?.audit_year_id),
    staleTime: 60_000,
    // Keep the previous page while the next loads, but never show another audit year's rows.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === params?.audit_year_id ? keepPreviousData(prev) : undefined),
  });
}

export function useHygieneFinding(id: string | null) {
  return useQuery({
    queryKey: hygieneQueryKeys.finding(id ?? ''),
    queryFn: () => fetchHygieneFinding(id as string),
    enabled: Boolean(id),
  });
}

export function useHygieneChecks() {
  return useQuery({ queryKey: hygieneQueryKeys.checks(), queryFn: fetchHygieneChecks, staleTime: 60 * 60_000 });
}

export function useHygieneAssignees(enabled: boolean) {
  return useQuery({ queryKey: hygieneQueryKeys.assignees(), queryFn: fetchHygieneAssignees, enabled, staleTime: 5 * 60_000 });
}

export function useHygieneEstimate(auditYearId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: hygieneQueryKeys.estimate(auditYearId ?? ''),
    queryFn: () => fetchHygieneAIEstimate(auditYearId as string),
    enabled: Boolean(auditYearId) && enabled,
    staleTime: 0,
  });
}

/** Refreshes everything shown for one audit year (summary, lists, open finding panels). */
function useInvalidateYear() {
  const queryClient = useQueryClient();
  return (auditYearId: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: hygieneQueryKeys.year(auditYearId) }),
      queryClient.invalidateQueries({ queryKey: [...hygieneQueryKeys.all, 'finding'] }),
    ]);
}

export function useHygieneAction() {
  const invalidate = useInvalidateYear();
  return useMutation({
    mutationFn: ({ id, action, body }: { id: string; action: HygieneAction; body: HygieneActionBody }) => postHygieneAction(id, action, body),
    // Returning the promise keeps the mutation pending until the refreshed list has landed.
    onSuccess: (_data, vars) => invalidate(vars.body.audit_year_id),
    onError: (error) => toastError(error, 'The change could not be saved.'),
  });
}

/** Starts a rule check or an AI review. A 409 returns the scan already running so the page follows it. */
export function useStartHygieneScan(onActive: (scan: HygieneScan) => void) {
  const invalidate = useInvalidateYear();
  return useMutation({
    mutationFn: ({ auditYearId, kind }: { auditYearId: string; kind: 'rules' | 'ai' }) => startHygieneScan(auditYearId, kind),
    onSuccess: async (res, vars) => {
      if (res.message) toast.success(res.message);
      if (res.scan && isScanActive(res.scan)) onActive(res.scan);
      await invalidate(vars.auditYearId);
    },
    onError: (error, vars) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        const scan = error.response.data?.scan as HygieneScan | undefined;
        if (scan?.id) {
          onActive(scan);
          toast(vars.kind === 'ai' ? 'An AI review is already running. Showing its progress.' : 'A check is already running; the page will refresh when it finishes.');
          // A rule check takes seconds: refresh shortly so its results show without a reload.
          if (vars.kind === 'rules') setTimeout(() => void invalidate(vars.auditYearId), 5000);
          return;
        }
      }
      toastError(error, vars.kind === 'ai' ? 'The AI review could not be started.' : 'The check could not run.');
    },
  });
}

export function useCancelHygieneScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelHygieneScan(id),
    onSuccess: (scan) => {
      queryClient.setQueryData(hygieneQueryKeys.scan(scan.id), (old: { scan: HygieneScan; stalled: boolean } | undefined) => ({ stalled: false, ...old, scan }));
      toast.success(scan.status === 'cancelled' ? 'The AI review was cancelled.' : 'The AI review will stop after the current batch.');
    },
    onError: (error) => toastError(error, 'The AI review could not be cancelled.'),
  });
}

/**
 * Polls one scan's status only (not the report): every 2s, backing off to 10s, until it finishes. When it
 * finishes, the year's data is refreshed once.
 */
export function useHygieneScanStatus(scanId: string | null, auditYearId: string | null) {
  const polls = useRef(0);
  const invalidate = useInvalidateYear();
  useEffect(() => {
    polls.current = 0;
  }, [scanId]);
  const query = useQuery({
    queryKey: hygieneQueryKeys.scan(scanId ?? ''),
    queryFn: () => {
      polls.current += 1;
      return fetchHygieneScan(scanId as string);
    },
    enabled: Boolean(scanId),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data && (!isScanActive(data.scan) || data.stalled)) return false;
      // A failed poll (network blip, restart) keeps polling at the slowest pace instead of freezing the progress.
      if (q.state.error) return 10_000;
      return Math.min(10_000, 2_000 + polls.current * 1_000);
    },
  });
  const status = query.data?.scan.status;
  const finished = Boolean(status && FINISHED.has(status));
  useEffect(() => {
    if (finished && auditYearId) void invalidate(auditYearId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, auditYearId]);
  return query;
}
